/**
 * Security utilities for authentication and input validation
 */

export const EMAIL_MAX_LENGTH = 254; // RFC 5321
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
export const INPUT_MAX_LENGTH = 500;

// Email validation regex (RFC 5322 compliant)
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Validates email format and length
 * @param {string} email - Email to validate
 * @returns {{isValid: boolean, error: string}}
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Email is required.' };
  }

  const trimmedEmail = email.trim();

  if (trimmedEmail.length === 0) {
    return { isValid: false, error: 'Email cannot be empty.' };
  }

  if (trimmedEmail.length > EMAIL_MAX_LENGTH) {
    return { isValid: false, error: `Email must be less than ${EMAIL_MAX_LENGTH} characters.` };
  }

  if (!EMAIL_REGEX.test(trimmedEmail)) {
    return { isValid: false, error: 'Please enter a valid email address.' };
  }

  return { isValid: true, error: '' };
};

/**
 * Sanitizes email by trimming and converting to lowercase
 * @param {string} email - Email to sanitize
 * @returns {string}
 */
export const sanitizeEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return '';
  }
  return email.trim().toLowerCase();
};

/**
 * Validates password length
 * @param {string} password - Password to validate
 * @returns {{isValid: boolean, error: string}}
 */
export const validatePasswordLength = (password) => {
  if (!password || typeof password !== 'string') {
    return { isValid: false, error: 'Password is required.' };
  }

  if (password.length < PASSWORD_MIN_LENGTH) {
    return { isValid: false, error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.` };
  }

  if (password.length > PASSWORD_MAX_LENGTH) {
    return { isValid: false, error: `Password must be less than ${PASSWORD_MAX_LENGTH} characters.` };
  }

  return { isValid: true, error: '' };
};

/**
 * Sanitizes string input by trimming and limiting length
 * @param {string} input - Input to sanitize
 * @param {number} maxLength - Maximum length (default: INPUT_MAX_LENGTH)
 * @returns {string}
 */
export const sanitizeInput = (input, maxLength = INPUT_MAX_LENGTH) => {
  if (!input || typeof input !== 'string') {
    return '';
  }
  return input.trim().slice(0, maxLength);
};

/**
 * Escapes HTML to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string}
 */
export const escapeHtml = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
};

/**
 * Validates and sanitizes URL parameter
 * @param {string} param - URL parameter value
 * @param {number} maxLength - Maximum length
 * @returns {string|null}
 */
export const sanitizeUrlParam = (param, maxLength = 500) => {
  if (!param || typeof param !== 'string') {
    return null;
  }
  try {
    const decoded = decodeURIComponent(param);
    const sanitized = sanitizeInput(decoded, maxLength);
    return sanitized.length > 0 ? sanitized : null;
  } catch (e) {
    // Invalid URL encoding
    return null;
  }
};

/**
 * Validates API URL from environment
 * @param {string} url - API URL to validate
 * @returns {boolean}
 */
export const isValidApiUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }
  try {
    const urlObj = new URL(url);
    // Only allow http/https protocols
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch (e) {
    return false;
  }
};

/**
 * Validates JWT token structure (basic check)
 * @param {string} token - JWT token
 * @returns {boolean}
 */
export const isValidJwtStructure = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  // JWT should have 3 parts separated by dots
  const parts = token.split('.');
  return parts.length === 3 && parts.every((part) => part.length > 0);
};

/**
 * Safely decodes JWT payload
 * @param {string} token - JWT token
 * @returns {object|null}
 */
export const decodeJwtPayload = (token) => {
  if (!isValidJwtStructure(token)) {
    return null;
  }

  try {
    const parts = token.split('.');
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (e) {
    return null;
  }
};

/**
 * Clears sensitive data from memory (best effort)
 * @param {string} value - Sensitive value to clear
 */
export const clearSensitiveData = (value) => {
  if (typeof value === 'string' && value.length > 0) {
    // Overwrite with random data (best effort)
    const random = Math.random().toString(36);
    return random;
  }
  return '';
};
